import { Error, Payments, ReceiptLong } from '@mui/icons-material'
import { Avatar, Box, Grid, Link, Stack, Typography, styled } from '@mui/material'
import { InvoiceCloudFrame, MButton, ProgressLoaders, Text } from 'components'
import { ROUTES_PATH, USER_TOKEN } from 'config'
import { color } from 'config/color'
import { useAuth } from 'context'
import dayjs from 'dayjs'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatValue, useLocalStorage } from 'utils'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'
export const Bill: React.FC = () => {
  const { account, currentAccount, getAccount, isAdmin, userEmail } = useAuth()
  const isMobile = useMobileBreakpoints()
  const navigate = useNavigate()
  const [authToken] = useLocalStorage(USER_TOKEN, '')
  const [prevAccountId, setPrevAccountId] = useState<string>('')

  useEffect(() => {
    if (
      prevAccountId === currentAccount?.accountId ||
      currentAccount?.accountId === account?.myAccount?.accountId
    ) {
      return
    }
    if (currentAccount?.accountId !== null) {
      void getAccount({
        AccessToken: authToken,
        accountId: currentAccount?.accountId,
        admin: isAdmin,
        email: userEmail
      })
    }
    setPrevAccountId(
      currentAccount?.accountId ?? account?.myAccount?.accountId ?? ''
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken, currentAccount])
  return (
    <>
      {account !== null
        ? (
          <Wrapper height="100%">
            <Box px={3} py={5}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                pb={3}
              >
                <Box display="flex" flexDirection="column" gap={1}>
                  <Text color={color.black} fontSize="18px" fontWeight="700">
                    Water Bill
                  </Text>
                  <Text color={color.grey[700]} fontSize="12px">
                    Account Number: {account?.myAccount.accountId}
                  </Text>
                </Box>
                {!isMobile && (
                  <InvoiceCloudFrame page={1}>
                    <MButton
                      variant="contained"
                      size="large"
                      rounded="true"
                      texttransform="none"
                    >
                      Pay Bill
                    </MButton>
                  </InvoiceCloudFrame>
                )}
              </Box>
              <Box display="flex" gap={2} mb={isMobile ? 2 : 0} flexDirection={isMobile ? 'column' : 'row'}>
                <Box flex={1} p={2} border={`solid 1px ${color.background[300]}`} borderRadius={1}>
                  <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    <Avatar sx={{ bgcolor: color.background[50], width: 32, height: 32 }}>
                      <Payments fontSize="small" sx={{ color: color.primary[600] }} />
                    </Avatar>
                    <Typography color={color.grey[900]} fontSize={16} fontWeight={600}>Balance Due</Typography>
                  </Stack>
                  <Typography color={color.grey[900]} fontSize={48} >
                    {account?.myAccount?.accountBalance != null &&
                      formatValue(account?.myAccount?.accountBalance)}
                  </Typography>
                  <Stack alignItems="center" width="fit-content" gap={0.5} p={0.5} direction="row" bgcolor={color.error[50]} borderRadius={1} border={`solid 1px ${color.error[100]}`} >
                    <Error sx={{ color: color.error[600], fontSize: 16 }} />
                    <Typography color={color.grey[800]} fontSize={14}>
                      Due Date:{' '}
                      {account?.myAccount?.dueDate != null &&
                        dayjs(account?.myAccount?.dueDate).format('MMMM D, YYYY')}
                    </Typography>
                  </Stack>
                </Box>
                <Box flex={1} p={2} border={`solid 1px ${color.background[300]}`} borderRadius={1}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Avatar sx={{ bgcolor: color.background[50], width: 32, height: 32 }}>
                        <ReceiptLong fontSize="small" sx={{ color: color.primary[600] }} />
                      </Avatar>
                      <Typography color={color.grey[900]} fontSize={16} fontWeight={600}>Details</Typography>
                    </Stack>
                    <Link style={{ cursor: 'pointer' }} fontSize={14} onClick={() => { navigate(ROUTES_PATH.BILLING) }}>
                      View Details
                    </Link>
                  </Stack>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography color={color.grey[700]} fontSize={14}>
                        Previous Bill Adjustments
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography fontWeight={600} color={color.grey[900]} textAlign="right" fontSize={14}>
                        {(account?.myAccount?.lateFees != null &&
                          formatValue(account?.myAccount?.lateFees)) ?? (account?.myAccount?.unbilledCharges?.lateFees != null &&
                            formatValue(account?.myAccount?.unbilledCharges?.lateFees))}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography color={color.grey[700]} fontSize={14}>
                        Previous Balance:
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography fontWeight={600} color={color.grey[900]} textAlign="right" fontSize={14}>
                        {(account?.myAccount?.previousBalance != null &&
                          formatValue(account?.myAccount?.previousBalance)) ?? (account?.myAccount?.accountBalance != null &&
                            formatValue(account?.myAccount?.accountBalance))}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography color={color.grey[700]} fontSize={14}>
                        Current Bill Balance
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography lineHeight={1} fontWeight={600} color={color.grey[900]} textAlign="right" fontSize={14}>
                        {(account?.myAccount?.latestBillAmount != null &&
                          formatValue(account?.myAccount?.latestBillAmount)) ?? (account?.myAccount?.endingBalOnBill != null &&
                            formatValue(account?.myAccount?.endingBalOnBill))}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography color={color.grey[700]} fontSize={14}>
                        Recent Payment
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography fontWeight={600} color={color.grey[900]} textAlign="right" fontSize={14}>
                        {(account?.myAccount?.latestPaymentReceived != null &&
                          formatValue(account?.myAccount?.latestPaymentReceived)) ?? (account?.myAccount?.latestPayment != null &&
                            formatValue(account?.myAccount?.latestPayment)) ?? (account?.myAccount?.unbilledCharges?.payments != null &&
                              formatValue(account?.myAccount?.unbilledCharges?.payments))}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
              {isMobile && (
                <InvoiceCloudFrame page={1}>
                  <MButton
                    variant="contained"
                    size="large"
                    rounded="true"
                    texttransform="none"
                    fullWidth
                  >
                    Pay Bill
                  </MButton>
                </InvoiceCloudFrame>
              )}
            </Box>
          </Wrapper>
        )
        : (
          <ProgressLoaders height="100%" />
        )}
    </>
  )
}

const Wrapper = styled(Box)({
  background: color.white,
  borderRadius: '4px'
})
